import { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import { type DialogBreakpoint, type DialogDefinedSize, type DialogHeight, DialogWidth } from './size.types';

export function dialogSizePropsToMuiProps(
  minWidth: { breakpoint: DialogBreakpoint; value: number },
  width: DialogWidth | undefined,
  maxWidth: DialogWidth | undefined,
  height: DialogHeight | undefined,
  maxHeight: DialogHeight | undefined
): Pick<MuiDialogProps, 'maxWidth' | 'fullWidth' | 'sx'> {
  const widthProps = getWidth(minWidth, width, maxWidth);
  return {
    ...widthProps,
    ...getHeight(height, maxHeight, widthProps.sx)
  };
}

function getHeight(
  height: DialogHeight | undefined,
  maxHeight: DialogHeight | undefined,
  sx?: Sx
): Pick<MuiDialogProps, 'sx'> {
  if (height === undefined && maxHeight === undefined) {
    return {};
  }
  if (sx) {
    return {
      sx: {
        ...sx,
        '.MuiDialog-paper': {
          ...sx['.MuiDialog-paper'],
          height,
          maxHeight
        }
      }
    };
  }
  return {
    sx: {
      '.MuiDialog-paper': {
        height,
        maxHeight
      }
    }
  };
}

function getWidth(
  minWidth: { breakpoint: DialogBreakpoint; value: number },
  width: DialogWidth | undefined,
  maxWidth: DialogWidth | undefined
): Pick<MuiDialogProps, 'maxWidth' | 'fullWidth'> & { sx?: Sx } {
  if (width !== undefined) {
    if (width === false) {
      return {
        fullWidth: false
      };
    }
    if (isBreakpoint(width)) {
      return { maxWidth: width, fullWidth: true };
    }
    return { maxWidth: false, fullWidth: true, sx: getSxValue({ width }) };
  }
  if (maxWidth !== undefined) {
    if (maxWidth === false) {
      return {
        maxWidth: false,
        fullWidth: false
      };
    }
    if (isBreakpoint(maxWidth)) {
      return {
        maxWidth,
        fullWidth: false,
        sx: getSxValue({
          minWidth: minWidth.value
        })
      };
    }
    return {
      maxWidth: false,
      fullWidth: false,
      sx: getSxValue({
        minWidth: minWidth.value,
        maxWidth
      })
    };
  }
  return {
    sx: getSxValue({
      minWidth: minWidth.value
    })
  };
}

const breakpoints: DialogBreakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];

function isBreakpoint(value: DialogWidth): value is DialogBreakpoint {
  return typeof value === 'string' && breakpoints.includes(value as DialogBreakpoint);
}

type Sx = {
  '.MuiDialog-paper': {
    minWidth?: DialogDefinedSize;
    width?: DialogDefinedSize;
    maxWidth?: DialogDefinedSize;
  };
};

function getSxValue(payload: {
  minWidth?: DialogDefinedSize;
  width?: DialogDefinedSize;
  maxWidth?: DialogDefinedSize;
}): Sx {
  return {
    '.MuiDialog-paper': payload
  };
}
